import React, { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { Panel } from 'primereact/panel'
import { Menu } from 'primereact/menu'
import { Button } from 'primereact/button'

import ConnectorStatus from '../../lib/connector-status/index'
import ConnectorInfo from '../../ui/chargepoints/ConnectorInfo'
import { ChargepointAssigned } from '../../lib/chargepoint-assigned'
import isSchukoEnabled from '../../lib/isSchukoEnabled'
import isAura from '../../lib/isAura'
import {
    ChargePoint,
    Connector,
    LoadBalancerGroup,
    useLoadBalancerGroupDeleteChargePointFromGroupMutation,
} from '../../common/redux/api/web-api'
import DropdownMenu, { DropdownMenuItem } from '../../common/components/DropdownMenu'
import EmptyListRefresh from '../../common/components/EmptyListRefresh'
import { isAdminDomain } from '../../lib/currentDomain'
import { EditIcon } from '../../common/icons/edit-icon'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../lib/table'
import { STableLink } from '../../lib/table/Table.styles'
import isOnline from '../../lib/isOnline'
import { isChargingConnector } from '../../lib/findChargingConnector'
import { theme } from '../../lib/theme'
import TrashIcon from '../../lib/icons/trash-icon/trash-icon'
import SignalIcon from '../../lib/icons/signal-icon/signal-icon'
import ConfirmationModal from '../../ui/ConfirmationModal'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import useText from '../../common/hooks/useText'

import LoadBalancerImportChargePoint from './loadBalancerImportChargePoint/LoadBalancerImportChargePoint'
import EditChargePointSettings from './EditChargePointSettings'

type LBChargePointListType = {
    chargePoints: ChargePoint[]
    isGroupActive: boolean
    isLoading: boolean
    isFetching: boolean
    group: LoadBalancerGroup
    updateChargers: () => void
}

const TdContainer = styled.div`
    min-width: max-content;
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const SStatusContainer = styled.div`
    display: table-caption;
    padding: 2px;
    margin-left: 0.75rem;
`

export default ({
    chargePoints: initialChargePoints,
    isGroupActive,
    isLoading,
    isFetching,
    group,
    updateChargers,
}: LBChargePointListType): ReactElement => {
    const text = useText()
    const [chargePoints, setChargePoints] = useState([...initialChargePoints])
    const [selectedChargePointId, setSelectedChargePointId] = useState<string>()
    const [openconfirmModal, setOpenConfirmModal] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [selectedChargePoint, setSelectedChargePoint] = useState<ChargePoint | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const menuRight = useRef(null)

    useEffect(() => {
        setChargePoints([...initialChargePoints])
    }, [initialChargePoints])

    const openModal = (chargePoint: ChargePoint) => {
        setSelectedChargePoint(chargePoint)
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        updateChargers()
    }

    const schukoEnabled = (chargePoint: ChargePoint) => {
        if (isAura(chargePoint)) return false
        if (!chargePoint.isOcpp) {
            return true
        }
        return isSchukoEnabled(chargePoint)
    }

    const [removeCharger] = useLoadBalancerGroupDeleteChargePointFromGroupMutation()
    const removeChargePointFromGroup = async (chargePointId: string) => {
        try {
            await removeCharger({ id: group?.id ?? 0, chargePointId: chargePointId.toString() }).unwrap()
            notifySuccess(text('msgChargerRemoved'))
        } catch (exception) {
            notifyError(text('msgCouldNotRemoveCharger'))
        } finally {
            setOpenConfirmModal(false)
        }
    }

    const getMenuItems = [
        {
            label: text('lblChargersImportModal'),
            command: () => {
                setShowImportModal(true)
            },
            disabled: isGroupActive,
            icon: 'pi pi-cloud-upload',
            className: 'auto',
        },
    ]

    return (
        <div>
            <Panel
                title={text('btnChargers')}
                data-testid="chargepoints-list-lBGroup"
                icons={
                    !isAdminDomain && (
                        <>
                            <Menu
                                model={getMenuItems}
                                popup
                                ref={menuRight}
                                id="popup_menu_right"
                                popupAlignment="right"
                                data-testid="menu-toggle-btn"
                            />

                            <Button
                                size="small"
                                icon="pi pi-refresh"
                                onClick={() => updateChargers()}
                                aria-haspopup
                                title={text('btnRefresh')}
                                className="p-button-plain mr-2"
                                data-testid="refresh-button"
                            />
                            <Button
                                size="small"
                                icon="pi pi-bars"
                                onClick={(event) => menuRight.current.toggle(event)}
                                aria-controls="popup_menu_right"
                                aria-haspopup
                                className="p-button-plain"
                                data-testid="menu-button"
                            />
                        </>
                    )
                }
                header={
                    <>
                        <> {text('btnChargers')}</>
                    </>
                }
            >
                {chargePoints?.length == 0 && (
                    <EmptyListRefresh
                        emptyText={text('msgNoChargePointsInLBGroup')}
                        isFetching={isFetching}
                        action={updateChargers}
                        label={text('btnRefresh')}
                        data-testid="chargepoints-empty-list"
                    />
                )}
                {group && chargePoints?.length > 0 && (
                    <div className="ml-4 mr-4">
                        <Table data-testid="chargepoints-list">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>{text('lblSerialNumber')}</TableHeaderCell>
                                    <TableHeaderCell>{text('lblStatus')}</TableHeaderCell>
                                    <TableHeaderCell>{text('lblSettings')}</TableHeaderCell>
                                    <TableHeaderCell>{text('lblOffline')}</TableHeaderCell>
                                    <TableHeaderCell>{text('lblAssigned')}</TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {!isLoading &&
                                    !isFetching &&
                                    chargePoints?.map((chargePoint) => (
                                        <TableRow key={chargePoint?.id}>
                                            <TableBodyCell>
                                                <STableLink
                                                    as={Link}
                                                    draggable="false"
                                                    type="button"
                                                    to={`/chargepoints/${chargePoint.id}`}
                                                    className="btn btn-link btn-xs"
                                                    data-testid="chargepoint-id"
                                                >
                                                    {chargePoint?.id}
                                                </STableLink>
                                            </TableBodyCell>

                                            <TableBodyCell data-testid="chargepoint-status">
                                                <SStatusContainer>
                                                    {chargePoint?.connectors?.map((connector: Connector) => {
                                                        if (!isOnline(chargePoint) && isChargingConnector(connector)) {
                                                            return (
                                                                <SignalIcon
                                                                    fill={theme.brandColors.CA_Coral}
                                                                    size={16}
                                                                    title={text('lblOfflineConnectorName', {
                                                                        name: connector?.name,
                                                                    })}
                                                                />
                                                            )
                                                        } else if (isOnline(chargePoint)) {
                                                            if (
                                                                connector?.connectorFunctionType === 'Schuko' &&
                                                                !schukoEnabled(chargePoint)
                                                            ) {
                                                                return null
                                                            }

                                                            return (
                                                                <ConnectorStatus
                                                                    connector={connector}
                                                                    isOcpp={chargePoint?.isOcpp}
                                                                    key={connector?.connectorId}
                                                                    productId={chargePoint?.productId}
                                                                />
                                                            )
                                                        }
                                                    })}
                                                </SStatusContainer>
                                            </TableBodyCell>
                                            <TableBodyCell data-testid="chargepoint-settings">
                                                {chargePoint?.connectors
                                                    ?.filter((connector) => isChargingConnector(connector))
                                                    ?.map((connector) => (
                                                        <TdContainer key={connector?.connectorId}>
                                                            <ConnectorInfo
                                                                showDetailedInfo={false}
                                                                key={connector?.connectorId}
                                                                chargePoint={chargePoint}
                                                                connector={connector}
                                                            />
                                                        </TdContainer>
                                                    ))}
                                            </TableBodyCell>
                                            <TableBodyCell data-testid="chargepoint-offline-settings">
                                                <TdContainer>
                                                    <div>
                                                        {chargePoint?.connectors
                                                            ?.filter((connector) => isChargingConnector(connector))
                                                            ?.map((connector) => (
                                                                <div
                                                                    key={connector?.connectorId}
                                                                    data-testid="connector-offline-settings"
                                                                >
                                                                    {connector?.loadBalancerOfflinePhase} -{' '}
                                                                    {connector?.loadBalancerOfflineCurrent} A
                                                                </div>
                                                            ))}
                                                    </div>
                                                </TdContainer>
                                            </TableBodyCell>
                                            <TableBodyCell data-testid="chargepoint-assigned">
                                                <TdContainer>
                                                    <ChargepointAssigned chargePoint={chargePoint} />
                                                </TdContainer>
                                            </TableBodyCell>
                                            <TableBodyCell data-testid="chargepoint-menu-settings">
                                                {!isAdminDomain && (
                                                    <DropdownMenu
                                                        id={chargePoint.id}
                                                        data-testid="menu-toggle-chargepoint"
                                                    >
                                                        <>
                                                            <DropdownMenuItem
                                                                onClick={() => {
                                                                    setOpenConfirmModal(true)
                                                                    setSelectedChargePointId(chargePoint?.id)
                                                                }}
                                                                text={text('btnRemove')}
                                                                icon={<TrashIcon size={14} />}
                                                                key="btnDeleteCharger"
                                                                disabled={isGroupActive}
                                                                data-testid="btn-delete-charger"
                                                                disabledReason={text('msgLbActive')}
                                                            />
                                                            <DropdownMenuItem
                                                                onClick={() =>
                                                                    !isGroupActive ? openModal(chargePoint) : null
                                                                }
                                                                text={text('lblLoadBalancerEditTooltip')}
                                                                icon={
                                                                    <EditIcon
                                                                        size={14}
                                                                        color={theme.brandColors.CA_Black}
                                                                    />
                                                                }
                                                                key="btnEditCharger"
                                                                disabled={isGroupActive}
                                                                data-testid="btn-edit-charger"
                                                                disabledReason={text('msgLbActive')}
                                                            />
                                                        </>
                                                    </DropdownMenu>
                                                )}
                                            </TableBodyCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Panel>

            {showModal && selectedChargePoint && (
                <EditChargePointSettings
                    chargePoint={selectedChargePoint}
                    closeModal={closeModal}
                    data-testid={`editChargePointModal`}
                />
            )}

            {showImportModal && group && (
                <LoadBalancerImportChargePoint
                    showModal={setShowImportModal}
                    group={group}
                    chargePointsInGroup={chargePoints}
                    updateChargePoints={() => updateChargers()}
                />
            )}
            {openconfirmModal && (
                <ConfirmationModal
                    title={text('msgRemoveChargerHeading')}
                    message={text('msgRemoveChargepointFromGroup')}
                    onConfirm={() => selectedChargePointId && removeChargePointFromGroup(selectedChargePointId)}
                    onClose={() => setOpenConfirmModal(false)}
                    confirmButtonText={text('btnConfirm')}
                />
            )}
        </div>
    )
}
